/**
 * buildJourneySteps — re-groups an existing claim form definition's data-record
 * steps (Vertragsdaten / Schaden / Schadenkosten / Polizei / Auszahlungen /
 * Ansprechpartner / Anhänge / Abschluß) into the click-dummy's journey taxonomy
 * (Schadenort → Ursache → Hergang → [Polizei] → Dokumente → Schadenhöhe →
 * Auszahlung → Kontakt → Bestätigung).
 *
 * It only RE-BUCKETS existing field descriptors by their `name` — field objects
 * (and their `valid`/`hidden`/`default`) are reused verbatim, so the MDDForm
 * engine's flattened validation / empty-model / transforms / submit are all
 * unchanged (they key off field `name`, not step grouping). Buckets with no
 * fields are dropped, which is how a step is "interjected" only for the Sparten
 * that actually carry those fields (e.g. Polizei).
 *
 * A field may pin its own bucket with a `journey` key; otherwise the resolver
 * below decides. Unknown fields fall to "hergang" so nothing is ever lost.
 *
 * Two deliberate departures from "verbatim":
 *  - `GROUP LABELS`: re-bucketing flattens the source steps, which throws away
 *    their headings. That matters for the catch-all "hergang" bucket, where a
 *    whole labelled block (e.g. formI's "Unfallgegner/Geschädigte Person") would
 *    otherwise render as unlabelled inputs directly beneath the preceding field
 *    ("Zeugen (Name und Anschrift)") and read as part of it. Each source group
 *    after the first in a bucket therefore re-emits its label as a `grouplabel`.
 *  - `VALID_OVERRIDES`: the legacy formdefs make every attachment mandatory, so
 *    there is no valid way to report a claim without documents. Mirror the
 *    schmidt wizard's `keine_medien` escape hatch (see buildClaimWizardSteps.js,
 *    which applies the same override to its own steps). Callers pass a
 *    cloneDeep'd fdef, so patching the descriptors here is local to this render.
 */

// Canonical journey order + copy (dummy tone). railLabel is the short stepper label.
const JOURNEY = [
  {
    key: "schadenort",
    label: "Wo ist der Schaden entstanden?",
    railLabel: "Schadenort",
    description: "Prüfen Sie das betroffene Objekt und die Adresse.",
    maxWidth: 680,
  },
  {
    key: "ursache",
    label: "Was war die Ursache?",
    railLabel: "Ursache",
    description: "Wählen Sie die Hauptursache des Schadens.",
    maxWidth: 680,
  },
  {
    key: "hergang",
    label: "Schadenhergang schildern",
    railLabel: "Hergang",
    description: "Beschreiben Sie den Vorfall so ausführlich wie möglich.",
    maxWidth: 680,
  },
  {
    key: "positionen",
    label: "Hergang & betroffener Hausrat",
    railLabel: "Positionen",
    description:
      "Erfassen Sie die beschädigten oder entwendeten Gegenstände einzeln.",
    maxWidth: 900,
  },
  {
    key: "polizei",
    label: "Polizeiliche Aufnahme",
    railLabel: "Polizei",
    description:
      "Falls der Vorfall polizeilich aufgenommen wurde, ergänzen Sie die Details.",
    maxWidth: 680,
  },
  {
    key: "dokumente",
    label: "Dokumente hochladen",
    railLabel: "Dokumente",
    description: "Fügen Sie Belege, Fotos und Kostenvoranschläge hinzu.",
    maxWidth: 720,
  },
  {
    key: "schadenhoehe",
    label: "Schadenhöhe angeben",
    railLabel: "Schadenhöhe",
    description: "Schätzen Sie die Schadenhöhe und erfassen Sie die Kosten.",
    maxWidth: 900,
  },
  {
    key: "auszahlung",
    label: "Auszahlung",
    railLabel: "Auszahlung",
    description: "Bankverbindung und steuerliche Angaben zur Regulierung.",
    maxWidth: 720,
  },
  {
    key: "kontakt",
    label: "Ansprechpartner",
    railLabel: "Kontakt",
    description: "Wer steht für Rückfragen zur Verfügung?",
    maxWidth: 680,
  },
  {
    key: "abschluss",
    label: "Angaben bestätigen",
    railLabel: "Bestätigung",
    description: "Bitte bestätigen Sie Ihre Angaben.",
    maxWidth: 680,
  },
];

const SCHADENORT = [
  "vertragsnummer",
  "gesellschaft",
  "schaden_ort",
  "schaden_adresszusatz",
  "schaden_besichtigung_adresse",
];
const SCHADENHOEHE = [
  "schaden_hoehe",
  "schaden_kostenvoranschlag",
  "schaden_rechnungsbetrag",
  "costs",
];
const DOKUMENTE = [
  "sonstiges_unterlagen",
  "schadenfotos",
  "kostenvoranschlag",
  "leckortungsbericht",
  "abtretungserklaerung",
];

// "Ich habe aktuell keine Medien" opt-out for the Dokumente step, so a claim can
// be reported with no attachments (the legacy formdefs require all three).
const NO_MEDIA = "getValue('keine_medien') === true";

const VALID_OVERRIDES = {
  sonstiges_unterlagen: `${NO_MEDIA} || isNotEmpty(item)`,
  schadenfotos: `${NO_MEDIA} || isNotEmpty(item)`,
  // Was a checkbox that had to be `true` to submit at all, despite its own
  // message only saying it "sollte" be uploaded.
  kostenvoranschlag: `${NO_MEDIA} || isEqual(item, true, 'Kostenvoranschlag oder Rechnung sollte hochgeladen werden')`,
};

const KEINE_MEDIEN = {
  control: "chiptoggle",
  name: "keine_medien",
  label: "Ich habe aktuell keine Medien",
  cols: 12,
};

/**
 * Blocks that describe someone who may not exist at all. The legacy formdefs make
 * the Unfallgegner/Geschädigte Person's name and address unconditionally required
 * (KFZ additionally demands their e-mail and Kennzeichen), yet the form never asks
 * whether there is a third party — so a single-vehicle or purely own-damage claim
 * cannot be submitted. Front each block with an explicit gate and hide its fields
 * until the gate is set; MDDForm skips validation for hidden fields (the same
 * mechanism the `fahrer_*` block already relies on).
 */
const GATED_BLOCKS = [
  {
    prefix: "geschaedigte_person_",
    gate: {
      control: "chiptoggle",
      name: "geschaedigter_vorhanden",
      label: "Es gibt einen Unfallgegner / Geschädigten",
      cols: 12,
    },
  },
];

const blockFor = (name) =>
  name ? GATED_BLOCKS.find((b) => name.startsWith(b.prefix)) : undefined;

const hiddenUnlessGate = (item, gateName) => {
  const gate = `isNotEqualOther('${gateName}', true)`;
  item.hidden = item.hidden ? `(${item.hidden}) || (${gate})` : gate;
};

// Two-phase split (schmidt flow): which journey steps the end customer fills vs.
// which the tenant broker completes. The customer describes the incident and
// uploads proof; the broker owns the insurance-technical + payout parts.
const PHASE = {
  schadenort: "customer",
  ursache: "customer",
  hergang: "customer",
  positionen: "customer",
  dokumente: "customer",
  kontakt: "customer",
  abschluss: "customer",
  polizei: "broker",
  schadenhoehe: "broker",
  auszahlung: "broker",
};

function bucketFor(item) {
  if (item.journey) return item.journey;

  const name = item.name;
  const control = item.control;

  // Nameless presentational controls key off their type.
  if (!name) {
    if (control === "sbwarning") return "schadenhoehe";
    if (control === "info") return "dokumente";
    if (control === "stehlgut_hinweise") return "dokumente";
    return "hergang";
  }

  if (control === "schadenpositionen") return "positionen";
  if (name === "schaden_ursache") return "ursache";
  if (name === "alles_korrekt") return "abschluss";
  if (SCHADENORT.includes(name)) return "schadenort";
  if (SCHADENHOEHE.includes(name)) return "schadenhoehe";
  if (DOKUMENTE.includes(name)) return "dokumente";
  if (name.startsWith("polizei")) return "polizei";
  if (name.startsWith("ansprechpartner")) return "kontakt";
  if (name.startsWith("auszahlung") || name === "vorsteuerabzugsberechtigt")
    return "auszahlung";

  // schaden_datum, grossschaden, schaden_schilderung, schaden_betroffene_sachen,
  // schaden_beschaedigte_sache, schaden_nummer_kd, schaden_abtretungserklärung,
  // and any Sparte-specific fields we don't explicitly place.
  return "hergang";
}

/**
 * Build the journey step array from the original stepper's `steps`.
 * @param {Array} originalSteps - the stepper control's `steps` (each {label, description, children}).
 * @returns {Array} journey steps [{key, label, railLabel, description, maxWidth, children}]
 */
export function buildJourneySteps(originalSteps = []) {
  // Flatten all field descriptors, preserving order and remembering which source
  // step each came from so its heading can be re-emitted (see file header).
  const fields = [];
  originalSteps.forEach((step) => {
    (step.children || []).forEach((child) =>
      fields.push({ item: child, group: step.label })
    );
  });

  const byBucket = new Map(JOURNEY.map((j) => [j.key, []]));
  const gatesPlaced = new Set();
  fields.forEach(({ item, group }) => {
    if (item.name && VALID_OVERRIDES[item.name]) {
      item.valid = VALID_OVERRIDES[item.name];
    }
    const key = bucketFor(item);
    const entries = byBucket.get(key) || byBucket.get("hergang");

    const block = blockFor(item.name);
    if (block) {
      // Emit the gate once, just ahead of the block's first field.
      if (!gatesPlaced.has(block.prefix)) {
        gatesPlaced.add(block.prefix);
        entries.push({ item: block.gate, group });
      }
      hiddenUnlessGate(item, block.gate.name);
    }

    entries.push({ item, group });
  });

  // Re-emit a source group's heading whenever it changes inside a bucket, except
  // for the bucket's first group (the journey step's own heading covers that) and
  // for groups that only contribute a stray field or two — those read as noise
  // rather than structure (e.g. the single "Anhänge" field that lands in hergang).
  const MIN_GROUP = 3;
  const withGroupLabels = (entries) => {
    const size = new Map();
    entries.forEach(({ group }) =>
      size.set(group, (size.get(group) || 0) + 1)
    );
    const out = [];
    let seenGroup = null;
    entries.forEach(({ item, group }) => {
      if (group && group !== seenGroup) {
        if (seenGroup !== null && size.get(group) >= MIN_GROUP)
          out.push({ control: "grouplabel", label: group });
        seenGroup = group;
      }
      out.push(item);
    });
    return out;
  };

  return JOURNEY.filter((j) => (byBucket.get(j.key) || []).length > 0).map(
    (j) => {
      const children = withGroupLabels(byBucket.get(j.key));
      if (j.key === "dokumente" && !children.some((c) => c.name === "keine_medien"))
        children.push(KEINE_MEDIEN);
      return {
        key: j.key,
        label: j.label,
        railLabel: j.railLabel,
        description: j.description,
        maxWidth: j.maxWidth,
        phase: PHASE[j.key] || "customer",
        children,
      };
    }
  );
}

/**
 * Return a copy-safe form definition whose stepper steps are the journey steps.
 * Mutates the passed fdef's stepper in place (callers pass a cloneDeep'd fdef).
 */
export function applyJourneyToFdef(fdef) {
  const stepper = fdef?.formdefinition?.find((c) => c.control === "stepper");
  if (stepper && Array.isArray(stepper.steps)) {
    stepper.steps = buildJourneySteps(stepper.steps);
  }
  return fdef;
}
